import { useEffect, useState } from 'react';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslations } from 'next-intl';

import { CounterOfferModal } from '../CounterOfferModal';
import { ExtendedLink } from '../ExtendedLink';
import { MakeOfferModal } from '../MakeOfferModal';
import { SecondaryPriceLine } from '../SecondaryPriceLine';

import { OfferTileButtons } from './OfferTileButton';
import { OfferTileMenu } from './OfferTileMenu';
import styles from './styles.module.css';

import { addCheckoutProducts } from '@/modules/checkout/helpers';
import { CheckoutType } from '@/modules/checkout/types';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { getCurrencySymbolFromCurrencyCode } from '@/modules/currencies/helpers';
import { getPictureFormatOrFallback } from '@/modules/images/helpers';
import { type PictureData } from '@/modules/images/types';
import { getOfferPriceTextKey } from '@/modules/offers/buyers/helpers/getOfferPriceTextKey';
import { getOfferStatuses } from '@/modules/offers/buyers/helpers/getOfferStatuses';
import { getOfferTileTitleKey } from '@/modules/offers/buyers/helpers/getOfferTileTitle';
import { mapOfferToCheckoutProduct } from '@/modules/offers/buyers/helpers/mapOfferToCheckoutProduct';
import { useOffersTracking } from '@/modules/offers/buyers/hooks/useOffersTracking';
import { type Offer, OfferRole } from '@/modules/offers/buyers/types';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';

type Props = Offer & {
  refetchParentQuery: () => void;
  includeBreakpointStyles?: boolean;
  showInlineButtons?: boolean;
  showImageMarginTop?: boolean;
  onCounterOfferSent?: () => void;
};

export function OfferTile({
  offer_display_status,
  expires_at,
  price_currency,
  offer_price,
  offer_id,
  prices,
  display_offer,
  product_id,
  product_slug,
  product_country_code,
  product_description,
  picture_data,
  variant_id,
  can_make_counter_offer,
  can_make_new_offer,
  offerers_highest_offer_value,
  sellers_lowest_offer_value,
  seller_username,
  checkout,
  onCounterOfferSent,
  refetchParentQuery,
  includeBreakpointStyles = true,
  showInlineButtons = false,
}: Props) {
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = useState(false);
  const [isCounterOfferModalOpen, setIsCounterOfferModalOpen] = useState(false);
  const [hasOfferSentSuccessfully, setHasOfferSentSuccessfully] =
    useState(false);
  const offersT = useTranslations('offers');
  const router = useExtendedRouter();
  const { sendOfferBuyNowEvent } = useOffersTracking();

  const checkoutProduct = mapOfferToCheckoutProduct({
    product_id,
    product_slug,
    product_country_code,
    product_description,
    variant_id,
    checkout,
  });

  const currency = price_currency as CurrencyCode;
  const originalPrice = prices?.original_price;
  const currentPrice = prices?.current_price;
  const hasPricing = Boolean(currentPrice);
  const canMakeNewOffer = can_make_new_offer && hasPricing;
  const canMakeCounterOffer = can_make_counter_offer && hasPricing;

  const titleKey = getOfferTileTitleKey(offer_display_status);
  const { isReceived, isRejected, isSold, isPurchaseable } =
    getOfferStatuses(offer_display_status);
  const priceTextKey = getOfferPriceTextKey(offer_display_status);
  const currencySymbol = getCurrencySymbolFromCurrencyCode(currency);
  const showOfferButtons =
    isPurchaseable || canMakeCounterOffer || canMakeNewOffer;
  const pictureData = picture_data as PictureData | undefined;
  const imgSrc = getPictureFormatOrFallback({
    pictureData,
    desiredFormat: 'P5',
  });
  const productImgSrc = getPictureFormatOrFallback({
    pictureData,
    desiredFormat: 'P2',
  });

  async function handleBuyClick() {
    if (!checkoutProduct) {
      return;
    }

    sendOfferBuyNowEvent({
      productId: product_id,
      currency,
      originalPrice: originalPrice?.price ?? '',
      currentPrice: currentPrice?.price ?? '',
      offerPrice: offer_price?.price ?? '',
    });

    addCheckoutProducts({
      products: [checkoutProduct],
      type: CheckoutType.SingleProduct,
    });

    router.push('/pay');
  }

  useEffect(() => {
    if (hasOfferSentSuccessfully) {
      refetchParentQuery();
    }
  }, [isMakeOfferModalOpen, isCounterOfferModalOpen]);

  return (
    <div
      className={clsx(styles.offerTileContainer, {
        [styles.offerTileBreakpointStyles]: includeBreakpointStyles,
      })}
    >
      {isReceived && (
        <OfferTileMenu
          offer_id={offer_id}
          refetchParentQuery={refetchParentQuery}
        />
      )}
      <div className={styles.offerTileRow}>
        <div className={styles.offerTileTextContainer}>
          <div>
            <Heading
              variant="sm"
              className={clsx(styles.offerTileTitle, {
                [styles.purchaseableOfferTitle]: isPurchaseable,
                [styles.rejectedOfferTitle]: isRejected,
              })}
            >
              {offersT(titleKey)}
            </Heading>
            {!isSold && (
              <Caption
                variant="lg-regular"
                className={clsx(styles.offerTileText, {
                  [styles.purchaseableOfferTileText]: isPurchaseable,
                })}
              >
                {`${
                  isPurchaseable ? offersT('BuyBefore') : offersT('Expires')
                } ${format(new Date(expires_at), `d MMMM 'at' haaa`)}`}
              </Caption>
            )}
          </div>
          {!isSold && display_offer && (
            <div>
              <div className={styles.offerTilePriceContainer}>
                <Caption
                  variant="lg-regular"
                  // eslint-disable-next-line no-restricted-syntax
                  data-testid="strikethrough"
                  className={styles.offerTileOriginalPrice}
                >
                  {currencySymbol}
                  {display_offer.original_price.amount}
                </Caption>
                <Heading variant="sm" className={styles.offerTilePrice}>
                  {offersT(priceTextKey)}: {currencySymbol}
                  {display_offer.offer_price.amount}
                </Heading>
              </div>
              {display_offer.secondary_price && (
                <SecondaryPriceLine
                  secondaryPrice={display_offer.secondary_price}
                />
              )}
            </div>
          )}
        </div>
        <ExtendedLink
          className={styles.offerTileLinkContainer}
          href={`/products/${product_slug}/`}
        >
          <LazyLoadImage alt="" src={imgSrc} height={320} width={320} />
        </ExtendedLink>
        {showOfferButtons && (
          <div
            className={clsx(styles.offerTileButtonsContainer, {
              [styles.offerTileButtonsContainerInline]: showInlineButtons,
            })}
          >
            <OfferTileButtons
              canMakeNewOffer={canMakeNewOffer}
              canMakeCounterOffer={canMakeCounterOffer}
              isPurchaseable={isPurchaseable}
              handleMakeNewOfferClick={() => setIsMakeOfferModalOpen(true)}
              handleMakeCounterOfferClick={() =>
                setIsCounterOfferModalOpen(true)
              }
              handlePurchaseClick={handleBuyClick}
            />
          </div>
        )}
      </div>
      {canMakeNewOffer && (
        <MakeOfferModal
          isAuthenticated
          isOpen={isMakeOfferModalOpen}
          setOpen={setIsMakeOfferModalOpen}
          productId={product_id}
          productPrice={{
            currencyName: currency,
            baseOriginalPrice: originalPrice?.price ?? '',
            baseCurrentPrice: currentPrice?.price ?? '',
            displayPrice: prices?.display_price,
          }}
          productImageHref={productImgSrc}
          selectedVariantId={variant_id}
          showOfferSentContent={hasOfferSentSuccessfully}
          onOfferSent={() => setHasOfferSentSuccessfully(true)}
        />
      )}
      {canMakeCounterOffer && (
        <CounterOfferModal
          isOpen={isCounterOfferModalOpen}
          setOpen={setIsCounterOfferModalOpen}
          currencySymbol={currencySymbol}
          productPrice={{
            currencyName: currency,
            baseOriginalPrice: originalPrice?.price ?? '',
            baseCurrentPrice: currentPrice?.price ?? '',
            displayPrice: prices?.display_price,
            offerersHighestOffer: offerers_highest_offer_value,
            sellersLowestOffer: sellers_lowest_offer_value,
          }}
          imageHref={productImgSrc}
          userRole={OfferRole.Buyer}
          productId={product_id}
          offerId={offer_id}
          variantId={variant_id}
          onOfferSent={() => {
            onCounterOfferSent?.();
            setHasOfferSentSuccessfully(true);
          }}
          offererUsername={seller_username}
        />
      )}
    </div>
  );
}
